import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Card, CardHeader } from '@/components/Card';
import { Button } from '@/components/Button';
import { Field } from '@/components/Field';
import { Input } from '@/components/Input';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useActiveCompanyId } from '@/lib/active-company';
import {
  useCompany,
  useUpdateCompany,
  type Company,
  type UpdateCompanyInput,
} from '@/lib/companies';
import {
  PROFILE_QUESTIONS,
  PROFILE_QUESTION_HELP,
  PROFILE_QUESTION_KEYS,
  questionShortLabel,
  type ProfileQuestionKey,
} from '@/lib/profile-questions';

interface CompanyFormValues {
  name: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  telephone: string;
}

export function CompanyTab() {
  const id = useActiveCompanyId();
  const company = useCompany(id);

  if (!id) return <p>Select a company first.</p>;
  if (company.isLoading || !company.data) return <p>Loading…</p>;

  // key={id}: both forms copy the company into local state once, on mount.
  // Remounting them on a company switch keeps a save from writing the
  // previous company's values onto the new one.
  return (
    <div className="flex flex-col gap-gap-element-lg">
      <Card>
        <CardHeader
          title="Company profile"
          description="Used on assessments, reports, and audit records."
        />
        <DetailsForm key={id} id={id} initial={company.data} />
      </Card>
      <Card>
        <CardHeader
          title="Compliance profile"
          description="Your answers determine which assessment items apply to your company."
        />
        <ProfileQuestionsForm key={id} id={id} initial={company.data} />
      </Card>
    </div>
  );
}

function DetailsForm({ id, initial }: { id: number; initial: Company }) {
  const update = useUpdateCompany(id);
  const { register, handleSubmit, formState } = useForm<CompanyFormValues>({
    defaultValues: {
      name: initial.name ?? '',
      addressLine1: initial.addressLine1 ?? '',
      addressLine2: initial.addressLine2 ?? '',
      city: initial.city ?? '',
      state: initial.state ?? '',
      postalCode: initial.postalCode ?? '',
      country: initial.country ?? '',
      telephone: initial.telephone ?? '',
    },
  });

  return (
    <form
      onSubmit={handleSubmit((data) => update.mutate(data))}
      className="grid gap-gap-element md:grid-cols-2"
    >
      <Field id="name" label="Company name" className="md:col-span-2">
        <Input id="name" {...register('name')} />
      </Field>
      <Field id="addressLine1" label="Address line 1" className="md:col-span-2">
        <Input id="addressLine1" {...register('addressLine1')} />
      </Field>
      <Field id="addressLine2" label="Address line 2" className="md:col-span-2">
        <Input id="addressLine2" {...register('addressLine2')} />
      </Field>
      <Field id="city" label="City">
        <Input id="city" {...register('city')} />
      </Field>
      <Field id="state" label="State / Region">
        <Input id="state" {...register('state')} />
      </Field>
      <Field id="postalCode" label="Postal code">
        <Input id="postalCode" {...register('postalCode')} />
      </Field>
      <Field id="country" label="Country">
        <Input id="country" {...register('country')} />
      </Field>
      <Field id="telephone" label="Telephone" className="md:col-span-2">
        <Input id="telephone" {...register('telephone')} />
      </Field>

      <FormStatus update={update} />

      <div className="md:col-span-2 flex justify-end">
        <Button type="submit" disabled={update.isPending || !formState.isDirty}>
          {update.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </form>
  );
}

type Answer = 'yes' | 'no' | 'unsure' | null;
type Answers = Record<ProfileQuestionKey, Answer>;

function readAnswers(company: Company): Answers {
  const unsure = new Set(company.unsureQuestions ?? []);
  return Object.fromEntries(
    PROFILE_QUESTION_KEYS.map((k): [ProfileQuestionKey, Answer] => {
      if (unsure.has(k)) return [k, 'unsure'];
      if (company[k] === true) return [k, 'yes'];
      if (company[k] === false) return [k, 'no'];
      return [k, null];
    }),
  ) as Answers;
}

function ProfileQuestionsForm({ id, initial }: { id: number; initial: Company }) {
  const update = useUpdateCompany(id);
  const [answers, setAnswers] = useState<Answers>(() => readAnswers(initial));
  const saved = readAnswers(initial);

  const dirty = PROFILE_QUESTION_KEYS.some((k) => answers[k] !== saved[k]);
  const unsureKeys = PROFILE_QUESTION_KEYS.filter((k) => answers[k] === 'unsure');

  const submit = () => {
    const payload: UpdateCompanyInput = { unsureQuestions: unsureKeys };
    for (const k of PROFILE_QUESTION_KEYS) {
      // "unsure" and unanswered both clear the Yes/No value to null so item
      // filtering keeps the dependent items visible.
      payload[k] = answers[k] === 'yes' ? true : answers[k] === 'no' ? false : null;
    }
    update.mutate(payload);
  };

  return (
    <div className="flex flex-col gap-gap-element">
      {unsureKeys.length > 0 ? <LegalReviewCallout keys={unsureKeys} /> : null}
      <p className="text-caption text-text-secondary">
        “How to tell” notes are general examples, not legal advice. If you're
        unsure, choose <span className="font-medium">Don't know</span> to flag the
        question for counsel.
      </p>
      <ul className="flex flex-col divide-y divide-border">
        {PROFILE_QUESTION_KEYS.map((k) => (
          <li
            key={k}
            className="flex flex-col gap-3 py-4 first:pt-0 md:flex-row md:items-start md:justify-between md:gap-8"
          >
            <div className="flex flex-col gap-1.5 md:pr-8">
              <p className="text-body-sm text-text-primary">{PROFILE_QUESTIONS[k]}</p>
              {PROFILE_QUESTION_HELP[k] ? (
                <details className="group">
                  <summary className="inline-flex cursor-pointer list-none items-center gap-1 text-caption font-medium text-accent hover:underline">
                    <span className="transition-transform group-open:rotate-90">
                      ›
                    </span>
                    How to tell
                  </summary>
                  <p className="mt-1.5 max-w-prose text-caption leading-relaxed text-text-secondary">
                    {PROFILE_QUESTION_HELP[k]}
                  </p>
                </details>
              ) : null}
            </div>
            <div className="shrink-0">
              <ProfileAnswerToggle
                label={PROFILE_QUESTIONS[k]}
                value={answers[k]}
                disabled={update.isPending}
                onChange={(value) =>
                  setAnswers((prev) => ({ ...prev, [k]: value }))
                }
              />
            </div>
          </li>
        ))}
      </ul>

      <FormStatus update={update} />

      <div className="flex justify-end">
        <Button type="button" onClick={submit} disabled={update.isPending || !dirty}>
          {update.isPending ? 'Saving…' : 'Save answers'}
        </Button>
      </div>
    </div>
  );
}

function LegalReviewCallout({ keys }: { keys: ProfileQuestionKey[] }) {
  return (
    <div className="rounded-md border border-warning/40 bg-warning/5 px-4 py-3">
      <p className="text-body-sm font-semibold text-warning">
        {keys.length} question{keys.length === 1 ? '' : 's'} marked “Don't know”
      </p>
      <p className="mt-1 text-body-sm text-text-secondary">
        These turn on legal determinations we can't make for you. We recommend
        consulting export-compliance counsel to resolve:{' '}
        <span className="font-medium text-text-primary">
          {keys.map((k) => questionShortLabel(k)).join(', ')}
        </span>
        .
      </p>
    </div>
  );
}

function ProfileAnswerToggle({
  value,
  onChange,
  label,
  disabled,
}: {
  value: Answer;
  onChange: (value: Answer) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <div className="flex gap-2" role="group" aria-label={label}>
      <AnswerButton
        selected={value === 'yes'}
        disabled={disabled}
        tone="accent"
        onClick={() => onChange('yes')}
      >
        Yes
      </AnswerButton>
      <AnswerButton
        selected={value === 'no'}
        disabled={disabled}
        tone="accent"
        onClick={() => onChange('no')}
      >
        No
      </AnswerButton>
      <AnswerButton
        selected={value === 'unsure'}
        disabled={disabled}
        tone="warning"
        onClick={() => onChange('unsure')}
      >
        Don't know
      </AnswerButton>
    </div>
  );
}

function AnswerButton({
  selected,
  disabled,
  tone,
  onClick,
  children,
}: {
  selected: boolean;
  disabled?: boolean;
  tone: 'accent' | 'warning';
  onClick: () => void;
  children: React.ReactNode;
}) {
  const selectedClass =
    tone === 'warning'
      ? 'border-warning bg-warning text-white'
      : 'border-accent bg-accent text-white';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={cn(
        'inline-flex h-8 items-center justify-center rounded-md border px-3 text-xs font-medium transition-colors disabled:opacity-50',
        selected
          ? selectedClass
          : 'border-border bg-surface text-text-secondary hover:border-text-secondary',
      )}
    >
      {children}
    </button>
  );
}

function FormStatus({
  update,
}: {
  update: { error: unknown; isSuccess: boolean };
}) {
  const error =
    update.error instanceof ApiError
      ? update.error.message
      : update.error
        ? 'Could not save changes.'
        : null;
  if (error) {
    return (
      <div className="rounded-md border border-danger/40 bg-danger/5 px-4 py-3 text-body-sm text-danger md:col-span-2">
        {error}
      </div>
    );
  }
  if (update.isSuccess) {
    return (
      <div className="rounded-md border border-success/40 bg-success/5 px-4 py-3 text-body-sm text-success md:col-span-2">
        Saved.
      </div>
    );
  }
  return null;
}
